import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ArrowDownTrayIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { dashboardService } from '../../services/dashboard.service';
import { useClients } from '../../hooks/useClients';
import { useProjects } from '../../hooks/useProjects';
import { format, parseISO } from 'date-fns';
import type { ReportFilter } from '../../types';

function formatDate(dateStr: string, granularity?: string): string {
  try {
    const d = parseISO(dateStr);
    if (granularity === 'day') return format(d, 'MMM d');
    if (granularity === 'week') return format(d, 'MMM d');
    return format(d, 'MMM yyyy');
  } catch {
    return dateStr;
  }
}

export default function Reports() {
  const [filters, setFilters] = useState<ReportFilter>({
    date_from: '',
    date_to: '',
    client_ids: [],
    project_ids: [],
  });

  const [selectedClient, setSelectedClient] = useState('');
  const [selectedProject, setSelectedProject] = useState('');

  const activeFilters = useMemo<ReportFilter>(() => {
    const f: ReportFilter = {};
    if (filters.date_from) f.date_from = filters.date_from;
    if (filters.date_to) f.date_to = filters.date_to;
    if (selectedClient) f.client_ids = [selectedClient];
    if (selectedProject) f.project_ids = [selectedProject];
    return f;
  }, [filters.date_from, filters.date_to, selectedClient, selectedProject]);

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['report', activeFilters],
    queryFn: () => dashboardService.getReport(activeFilters),
  });

  const { data: clientsData } = useClients({ pageSize: 100 });
  const { data: projectsData } = useProjects({ pageSize: 100 });

  const clients = clientsData?.data ?? [];
  const projects = projectsData?.data ?? [];

  // Format CSAT trend for chart
  const csatTrendData = useMemo(() => {
    const raw = reportData?.csat_trend ?? [];
    const gran = reportData?.granularity;
    return raw.map((point: any) => ({
      date: formatDate(point.date, gran),
      score: Number(point.score ?? 0),
      count: Number(point.count ?? 0),
    }));
  }, [reportData?.csat_trend, reportData?.granularity]);

  // Format Response Rate trend for chart
  const responseRateTrendData = useMemo(() => {
    const raw = reportData?.response_rate_trend ?? [];
    const gran = reportData?.granularity;
    return raw.map((point: any) => ({
      date: formatDate(point.date, gran),
      rate: Number(point.response_rate ?? 0),
    }));
  }, [reportData?.response_rate_trend, reportData?.granularity]);

  // Period description
  const periodLabel = useMemo(() => {
    if (filters.date_from && filters.date_to) {
      return `${filters.date_from} — ${filters.date_to}`;
    } else if (filters.date_from) {
      return `From ${filters.date_from}`;
    } else if (filters.date_to) {
      return `Until ${filters.date_to}`;
    }
    return 'Last 90 days';
  }, [filters.date_from, filters.date_to]);

  const handleExport = async (exportFormat: 'pdf' | 'csv' | 'xlsx') => {
    try {
      const blob = await dashboardService.exportReport(activeFilters, exportFormat);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `csat-report.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Export not yet implemented
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            CSAT analytics and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg bg-white p-4 shadow ring-1 ring-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-700">Filters</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="date_from" className="block text-sm font-medium text-gray-700">
              Date From
            </label>
            <input
              type="date"
              id="date_from"
              value={filters.date_from ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="date_to" className="block text-sm font-medium text-gray-700">
              Date To
            </label>
            <input
              type="date"
              id="date_to"
              value={filters.date_to ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="client_filter" className="block text-sm font-medium text-gray-700">
              Client
            </label>
            <select
              id="client_filter"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="project_filter" className="block text-sm font-medium text-gray-700">
              Project
            </label>
            <select
              id="project_filter"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {reportData && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
            <p className="text-sm font-medium text-gray-500">CSAT Score</p>
            <p className={`mt-2 text-3xl font-bold ${
              (reportData.overall_csat ?? 0) >= 80 ? 'text-green-600' :
              (reportData.overall_csat ?? 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {Number(reportData.overall_csat ?? 0).toFixed(1)}%
            </p>
            <p className="mt-1 text-xs text-gray-400">{periodLabel}</p>
          </div>
          <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
            <p className="text-sm font-medium text-gray-500">Response Rate</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {Number(reportData.response_rate ?? 0).toFixed(1)}%
            </p>
            <p className="mt-1 text-xs text-gray-400">Completed / Sent</p>
          </div>
          <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
            <p className="text-sm font-medium text-gray-500">Total Responses</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {reportData.total_responses ?? 0}
            </p>
          </div>
          <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
            <p className="text-sm font-medium text-gray-500">Clients</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {reportData.client_breakdown?.length ?? 0}
            </p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
        </div>
      )}

      {/* Charts */}
      {reportData && (
        <div className="space-y-6">
          {/* CSAT Trend Chart */}
          {csatTrendData.length > 0 && (
            <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
              <h3 className="mb-4 text-lg font-medium text-gray-900">CSAT Score Trend</h3>
              <p className="mb-2 text-xs text-gray-400">Monthly average CSAT score (%)</p>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={csatTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(value: any) => [`${Number(value).toFixed(1)}%`, 'CSAT']} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#4f46e5"
                      strokeWidth={2}
                      dot={{ fill: '#4f46e5', r: 4 }}
                      name="CSAT Score (%)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Response Rate Trend */}
          {responseRateTrendData.length > 0 && (
            <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
              <h3 className="mb-4 text-lg font-medium text-gray-900">Response Rate Trend</h3>
              <p className="mb-2 text-xs text-gray-400">Monthly response rate (%)</p>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={responseRateTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(value: any) => [`${value}%`, 'Response Rate']} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="rate"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ fill: '#10b981', r: 4 }}
                      name="Response Rate (%)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Per-Client Comparison */}
          {(reportData.client_breakdown ?? []).length > 0 && (
            <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
              <h3 className="mb-4 text-lg font-medium text-gray-900">CSAT by Client</h3>
              <p className="mb-2 text-xs text-gray-400">Average CSAT score per client (%)</p>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData.client_breakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="client_name" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(value: any, name: string) => [
                      name === 'CSAT Score' ? `${Number(value).toFixed(1)}%` : value,
                      name
                    ]} />
                    <Legend />
                    <Bar
                      dataKey="csat_score"
                      fill="#4f46e5"
                      name="CSAT Score"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="response_count"
                      fill="#10b981"
                      name="Responses"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Client Breakdown Table */}
          {(reportData.client_breakdown ?? []).length > 0 && (
            <div className="rounded-lg bg-white shadow ring-1 ring-gray-200">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">Client Details</h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">CSAT Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Responses</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(reportData.client_breakdown ?? []).map((client: any) => (
                    <tr key={client.client_id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{client.client_name}</td>
                      <td className="px-6 py-4">
                        {client.csat_score !== null ? (
                          <span className={`text-sm font-semibold ${
                            client.csat_score >= 80 ? 'text-green-600' :
                            client.csat_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {Number(client.csat_score).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">No data</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{client.response_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* No data message */}
          {csatTrendData.length === 0 && (reportData.client_breakdown ?? []).length === 0 && (
            <div className="rounded-lg bg-white py-12 text-center shadow ring-1 ring-gray-200">
              <p className="text-gray-500">No survey data found for the selected period and filters.</p>
              <p className="mt-1 text-sm text-gray-400">Try expanding the date range or changing client/project filter.</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !reportData && (
        <div className="rounded-lg bg-white py-12 text-center shadow ring-1 ring-gray-200">
          <p className="text-gray-500">No report data available. Try adjusting your filters.</p>
        </div>
      )}
    </div>
  );
}
